import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const links = [
  { id: "performance", label: "Performance", route: "/performance" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "framework", label: "Framework" },
  { id: "ideal-client", label: "Ideal Client" },
  { id: "contact", label: "Contact", route: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id, route) => {
    setOpen(false);
    if (route) {
      if (location.pathname !== route) {
        navigate(route);
        window.scrollTo({ top: 0, behavior: "instant" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goContact = () => {
    setOpen(false);
    if (location.pathname !== "/contact") {
      navigate("/contact");
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="ssg-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="ssg-logo"
          onClick={goHome}
          className="flex items-center gap-3 group"
          aria-label="Smith Strategic Group"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 bg-black overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/pkgt7i93_489F0D9A-2A77-47CF-B54B-18E9E2CA80E0.png"
              alt="SSG"
              className="w-10 h-10 object-contain"
            />
          </span>
          <span className="font-display tracking-tight text-[17px] font-bold leading-none">
            Smith Strategic Group
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id, l.route)}
              className="text-[13px] tracking-wider uppercase font-semibold text-neutral-700 hover:text-black ssg-link-underline"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-work"
            onClick={goContact}
            className="px-5 py-3 bg-[#0A0A0A] text-white text-[11px] tracking-[0.2em] uppercase font-bold rounded-none hover:bg-[#9B1C1C] transition-colors"
          >
            Work With Me
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.6} /> : <Menu size={22} strokeWidth={1.6} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-black/5 bg-white"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id, l.route)}
                className="text-left text-sm tracking-wider uppercase font-semibold text-neutral-800"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={goContact}
              className="mt-2 px-5 py-3 bg-[#0A0A0A] text-white text-[11px] tracking-[0.2em] uppercase font-bold"
            >
              Work With Me
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
